import type { IWalletKit } from '@reown/walletkit';
import { PublicKey } from '@solana/web3.js';
import * as multisig from '@sqds/multisig';
import { env } from '@/lib/env';
import { useWalletConnectStore, type WcMethod } from '@/stores/walletConnect';
import { useWalletStore } from '@/stores/wallet';
import { useMultisigStore } from '@/stores/multisig';
import { getConfig } from '@/lib/config';

let kitInstance: IWalletKit | null = null;

/**
 * Resolve the Squads vault PDA for the currently active multisig. This is the
 * address we advertise to dApps over WalletConnect — the multisig PDA itself
 * is owned by the Squads program and cannot hold SPL tokens or be used as a
 * transaction signer (sending tokens to it gives "invalid account" in any
 * SPL-aware wallet).
 *
 * Returns null if the multisig isn't selected yet or `/config` is unreachable.
 */
async function resolveVaultPda(multisigPda: string): Promise<string | null> {
  try {
    const cfg = await getConfig();
    const programId = new PublicKey(cfg.programId);
    const [vault] = multisig.getVaultPda({
      multisigPda: new PublicKey(multisigPda),
      index: 0,
      programId,
    });
    return vault.toBase58();
  } catch (err) {
    console.error('[walletconnect] failed to derive vault PDA', err);
    return null;
  }
}

const SUPPORTED_METHODS: WcMethod[] = [
  'solana_signMessage',
  'solana_signTransaction',
  'solana_signAndSendTransaction',
];

function buildSolanaChainId(cluster: string): string {
  switch (cluster) {
    case 'mainnet-beta':
      return 'solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp';
    case 'devnet':
      return 'solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1';
    case 'testnet':
      return 'solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z';
    default:
      return `solana:${cluster}`;
  }
}

export async function initWalletConnect(): Promise<IWalletKit | null> {
  if (kitInstance) return kitInstance;
  if (typeof window === 'undefined') return null;

  const wcStore = useWalletConnectStore.getState();
  const projectId = env.walletConnectProjectId;
  if (!projectId) {
    wcStore.setError('VITE_PUBLIC_WALLETCONNECT_PROJECT_ID not configured');
    return null;
  }

  if (wcStore.initializing) return null;
  wcStore.setInitializing(true);
  try {
    const [{ Core }, { WalletKit }] = await Promise.all([
      import('@walletconnect/core'),
      import('@reown/walletkit'),
    ]);
    const core = new Core({ projectId });
    const kit = await WalletKit.init({
      core,
      metadata: {
        name: 'Avocado Solana',
        description: 'Avocado Solana — 2FA wallet for Solana, TOTP-gated',
        url: 'https://' + env.appDomain,
        icons: ['https://' + env.appDomain + '/logo-sign.svg'],
      },
    });
    kitInstance = kit;

    kit.on('session_proposal', async (proposal) => {
      try {
        const wallet = useWalletStore.getState();
        const ms = useMultisigStore.getState();
        if (!ms.activePda || !wallet.publicKey()) {
          await kit.rejectSession({
            id: proposal.id,
            reason: { code: 5001, message: 'No active multisig — set one up first' },
          });
          return;
        }
        // The address exposed to dApps is the Squads VAULT PDA, NOT the
        // multisig config account and NOT the user's signer wallet:
        //   - multisig PDA is owned by the Squads program → cannot hold tokens
        //     or be a tx signer; sending funds to it fails ("invalid account").
        //   - user wallet would defeat the 2FA gate entirely (single-sig).
        //   - vault PDA is the on-chain account that actually holds funds and
        //     becomes the signer for any cross-program invocation via
        //     `vaultTransactionExecute`.
        const vaultPda = await resolveVaultPda(ms.activePda);
        if (!vaultPda) {
          await kit.rejectSession({
            id: proposal.id,
            reason: { code: 5002, message: 'Could not derive vault address — try again in a moment' },
          });
          return;
        }
        const chainId = buildSolanaChainId(env.solanaCluster);
        const session = await kit.approveSession({
          id: proposal.id,
          namespaces: {
            solana: {
              accounts: [`${chainId}:${vaultPda}`],
              methods: SUPPORTED_METHODS,
              events: [],
              chains: [chainId],
            },
          },
        });
        useWalletConnectStore.getState().upsertDapp({
          topic: session.topic,
          name: proposal.params.proposer.metadata.name,
          url: proposal.params.proposer.metadata.url,
          iconUrl: proposal.params.proposer.metadata.icons[0] ?? null,
          connectedAt: Date.now(),
        });
      } catch (err) {
        console.error('[walletconnect] session_proposal failed', err);
      }
    });

    kit.on('session_request', (event) => {
      const method = event.params.request.method as WcMethod;
      if (!SUPPORTED_METHODS.includes(method)) {
        kit.respondSessionRequest({
          topic: event.topic,
          response: {
            id: event.id,
            jsonrpc: '2.0',
            error: { code: 4001, message: `Method ${method} not supported` },
          },
        });
        return;
      }
      const wcState = useWalletConnectStore.getState();
      const dapp = wcState.connectedDapps.find((d) => d.topic === event.topic);
      wcState.enqueue({
        id: event.id,
        topic: event.topic,
        chainId: event.params.chainId,
        method,
        params: event.params.request.params,
        origin: dapp?.url ?? null,
      });
    });

    kit.on('session_delete', (event) => {
      useWalletConnectStore.getState().removeDapp(event.topic);
    });

    // Reconcile any sessions persisted in localStorage from a previous boot.
    // WalletKit re-hydrates them from its own keyValueStorage but the
    // `connectedDapps` store list is rebuilt from scratch each session. Also,
    // historical sessions from before the vault-PDA fix advertised the
    // multisig config account to dApps — those need to be force-disconnected
    // so the dApp drops the wrong address and the user re-pairs cleanly.
    const ms = useMultisigStore.getState();
    void reconcileExistingSessions(kit, ms.activePda, env.solanaCluster);

    useWalletConnectStore.getState().setInitialized(true);
    return kit;
  } catch (err) {
    useWalletConnectStore.getState().setError((err as Error).message);
    console.error('[walletconnect] init failed', err);
    return null;
  } finally {
    useWalletConnectStore.getState().setInitializing(false);
  }
}

/**
 * Walk every active WalletKit session and:
 *   1. Re-publish it into the connectedDapps list (so the UI shows
 *      pairings that survived a page reload).
 *   2. Force-disconnect any session whose advertised Solana account doesn't
 *      match the current vault PDA. This catches stale sessions from before
 *      the vault-PDA fix went live, where the dApp still thinks our address
 *      is the Squads multisig config account.
 */
async function reconcileExistingSessions(
  kit: IWalletKit,
  activeMultisigPda: string | null,
  cluster: string,
): Promise<void> {
  let sessions: Record<string, unknown> = {};
  try {
    sessions =
      (kit as unknown as { getActiveSessions: () => Record<string, unknown> }).getActiveSessions() ??
      {};
  } catch (err) {
    console.warn('[walletconnect] getActiveSessions failed', err);
    return;
  }
  if (Object.keys(sessions).length === 0) return;

  const expected = activeMultisigPda ? await resolveVaultPda(activeMultisigPda) : null;
  const expectedAccountSuffix = expected ? `:${expected}` : null;
  const chainId = buildSolanaChainId(cluster);

  for (const [topic, raw] of Object.entries(sessions)) {
    const s = raw as {
      namespaces?: { solana?: { accounts?: string[] } };
      peer?: { metadata?: { name?: string; url?: string; icons?: string[] } };
    };
    const accounts = s.namespaces?.solana?.accounts ?? [];
    const matches =
      expectedAccountSuffix !== null &&
      accounts.length > 0 &&
      accounts.every(
        (a) => a === `${chainId}${expectedAccountSuffix}` || a.endsWith(expectedAccountSuffix),
      );

    if (matches) {
      const meta = s.peer?.metadata;
      useWalletConnectStore.getState().upsertDapp({
        topic,
        name: meta?.name ?? 'Unknown dApp',
        url: meta?.url ?? '',
        iconUrl: meta?.icons?.[0] ?? null,
        connectedAt: Date.now(),
      });
      continue;
    }

    try {
      await kit.disconnectSession({
        topic,
        reason: {
          code: 6000,
          message: 'Connected address migrated to Squads vault PDA — please re-pair.',
        },
      });
      useWalletConnectStore.getState().removeDapp(topic);
      console.info('[walletconnect] dropped stale session', topic);
    } catch (err) {
      console.warn('[walletconnect] failed to drop stale session', topic, err);
    }
  }
}

export async function pairUri(uri: string): Promise<void> {
  const kit = await initWalletConnect();
  if (!kit) throw new Error('WalletKit not initialized');
  await kit.pair({ uri });
}

export function getWalletKit(): IWalletKit | null {
  return kitInstance;
}
